// Client-side auth UI cache (sessionStorage). HttpOnly app_session is set by the backend.

const AUTH_STORAGE_PREFIX = "nova_auth:";
const LEGACY_AUTH_COOKIES = [
  "access_token",
  "id_token",
  "refresh_token",
  "app_session",
  "userId",
  "userid",
  "email",
  "userName",
  "username",
  "profilepic",
  "role",
  "roles",
  "role_code",
  "role_codes",
  "tenantid",
  "tenantId",
  "tenant",
  "tenantName",
  "menuItems",
  "menuitems",
  "subid",
  "firstname",
  "lastname",
  "phonenumber",
  "selectedSessionId",
];

const KEY_ALIASES = {
  userId: "userid",
  userName: "username",
  tenantName: "tenant",
  tenantId: "tenantid",
  role_codes: "role_code",
  menuItems: "menuitems",
  selectedsessionid: "selectedSessionId",
  tenant_name: "tenant",
  tenant_id: "tenantid",
};

let legacyCookiesMigrated = false;

function normalizeKey(key) {
  if (!key) return key;
  return KEY_ALIASES[key] || KEY_ALIASES[key.toLowerCase()] || key;
}

function storageKey(name) {
  return `${AUTH_STORAGE_PREFIX}${normalizeKey(name)}`;
}

function serializeValue(value) {
  if (value === null || value === undefined) return null;
  if (Array.isArray(value) || (typeof value === "object" && value !== null)) {
    return JSON.stringify(value);
  }
  return String(value);
}

function deserializeValue(raw) {
  if (raw === null || raw === undefined || raw === "") return null;
  const trimmed = String(raw).trim();
  // Only parse structured JSON. A digits-only phone must stay a string;
  // JSON.parse("91789…") returns a number, and that number has no string methods.
  const isJson =
    (trimmed.startsWith("{") && trimmed.endsWith("}")) ||
    (trimmed.startsWith("[") && trimmed.endsWith("]")) ||
    (trimmed.startsWith('"') && trimmed.endsWith('"')) ||
    trimmed === "true" ||
    trimmed === "false" ||
    trimmed === "null";
  if (!isJson) return raw;
  try {
    return JSON.parse(trimmed);
  } catch {
    return raw;
  }
}

function readLegacyCookie(name) {
  const nameEQ = `${name}=`;
  const ca = document.cookie.split(";");

  for (let i = 0; i < ca.length; i++) {
    let c = ca[i];
    while (c.charAt(0) === " ") c = c.substring(1);
    if (c.indexOf(nameEQ) === 0) {
      return decodeURIComponent(c.substring(nameEQ.length));
    }
  }
  return null;
}

function deleteLegacyCookie(name) {
  const hostname = window.location.hostname;
  const domain = hostname.replace(/^www\./, "");
  const expires = "Thu, 01 Jan 1970 00:00:00 GMT";

  document.cookie = `${name}=; expires=${expires}; path=/`;
  document.cookie = `${name}=; expires=${expires}; path=/; domain=${domain}`;
  document.cookie = `${name}=; expires=${expires}; path=/; domain=www.${domain}`;
  if (window.location.protocol === "https:") {
    document.cookie = `${name}=; expires=${expires}; path=/; domain=${domain}; Secure`;
    document.cookie = `${name}=; expires=${expires}; path=/; Secure`;
  }
}

function migrateLegacyCookiesOnce() {
  if (legacyCookiesMigrated || typeof window === "undefined") return;
  legacyCookiesMigrated = true;

  LEGACY_AUTH_COOKIES.forEach((cookieName) => {
    const legacyValue = readLegacyCookie(cookieName);
    if (legacyValue === null || legacyValue === "") return;

    const normalized = normalizeKey(cookieName);
    if (!sessionStorage.getItem(storageKey(normalized))) {
      sessionStorage.setItem(storageKey(normalized), legacyValue);
    }
    deleteLegacyCookie(cookieName);
  });
}

function readStorageValue(name) {
  migrateLegacyCookiesOnce();
  const raw = sessionStorage.getItem(storageKey(name));
  return deserializeValue(raw);
}

function writeStorageValue(name, value) {
  migrateLegacyCookiesOnce();
  const key = storageKey(name);

  if (value === null || value === undefined || value === "") {
    sessionStorage.removeItem(key);
    return;
  }

  sessionStorage.setItem(key, serializeValue(value));
}

export function getTenantNameForHeader() {
  return (
    readStorageValue("tenant") ||
    readStorageValue("tenant_name") ||
    null
  );
}

export const cookieUtils = {
  getCookie: (name) => {
    const fromStorage = readStorageValue(name);
    if (fromStorage !== null && fromStorage !== undefined && fromStorage !== "") {
      return typeof fromStorage === "string" ? fromStorage : serializeValue(fromStorage);
    }
    return readLegacyCookie(name);
  },

  setCookie: (name, value) => {
    writeStorageValue(name, value);
  },

  deleteCookie: (name) => {
    writeStorageValue(name, null);
    deleteLegacyCookie(name);
  },

  clearAuthCookies: () => {
    migrateLegacyCookiesOnce();

    Object.keys(sessionStorage).forEach((key) => {
      if (key.startsWith(AUTH_STORAGE_PREFIX)) {
        sessionStorage.removeItem(key);
      }
    });

    LEGACY_AUTH_COOKIES.forEach((cookieName) => {
      deleteLegacyCookie(cookieName);
    });
  },

  saveAuthData: (authData) => {
    if (!authData) return;

    const {
      id,
      subid,
      email,
      firstname,
      lastname,
      username,
      userName,
      role,
      role_code,
      role_codes,
      tenant_name,
      tenant_id,
      profilepic,
      phonenumber,
      createdate,
      tenant_created_at,
      menuitems,
      selectedSessionId,
      app_session,
      roles,
      has_admin_access,
      document_access,
      is_impersonating,
      impersonated_by,
    } = authData;

    if (app_session) {
      console.warn(
        "Ignoring token-like values in frontend storage. Auth tokens must remain HttpOnly.",
      );
    }

    const resolvedUserId = id ?? authData.userid ?? authData.userId;
    const resolvedUserName =
      username ||
      userName ||
      [firstname, lastname].filter(Boolean).join(" ") ||
      email;
    const resolvedTenantId = tenant_id ?? authData.tenantId;
    const resolvedTenantName = tenant_name ?? authData.tenantName;
    const resolvedCognitoSub = subid ?? authData.cognitoSub;
    const resolvedRoleCodes =
      (Array.isArray(role_code) && role_code.length > 0 && role_code) ||
      (Array.isArray(role_codes) && role_codes.length > 0 && role_codes) ||
      [];

    if (resolvedUserId) writeStorageValue("userid", resolvedUserId.toString());
    if (email) writeStorageValue("email", email);
    if (resolvedUserName) writeStorageValue("username", resolvedUserName);
    if (profilepic) writeStorageValue("profilepic", profilepic);
    if (resolvedTenantId) writeStorageValue("tenantid", resolvedTenantId.toString());
    if (resolvedTenantName) writeStorageValue("tenant", resolvedTenantName);
    if (resolvedCognitoSub) writeStorageValue("subid", resolvedCognitoSub);
    if (firstname) writeStorageValue("firstname", firstname);
    if (lastname) writeStorageValue("lastname", lastname);
    if (phonenumber) writeStorageValue("phonenumber", phonenumber);
    if (createdate) writeStorageValue("createdate", createdate);
    if (tenant_created_at) writeStorageValue("tenant_created_at", tenant_created_at);
    if (selectedSessionId) {
      writeStorageValue("selectedSessionId", selectedSessionId.toString());
    }
    // Always overwrite role fields when present so demotions clear stale values.
    if (role_code !== undefined || role_codes !== undefined) {
      writeStorageValue("role_code", resolvedRoleCodes);
    }
    if (roles !== undefined) {
      writeStorageValue("roles", Array.isArray(roles) ? roles : []);
    }
    if (role !== undefined) {
      if (Array.isArray(role) && role.length > 0) {
        writeStorageValue("role", role);
      } else if (typeof role === "string" && role.trim()) {
        writeStorageValue("role", role);
      } else if (resolvedRoleCodes.includes("ADM")) {
        writeStorageValue("role", "Admin");
      } else if (Array.isArray(roles) && roles.length > 0) {
        writeStorageValue("role", roles[0].role || roles[0].rolecode);
      } else if (resolvedRoleCodes.length > 0) {
        writeStorageValue("role", "Employee");
      } else {
        writeStorageValue("role", null);
      }
    } else if (role_code !== undefined || role_codes !== undefined || roles !== undefined) {
      if (resolvedRoleCodes.includes("ADM")) {
        writeStorageValue("role", "Admin");
      } else if (Array.isArray(roles) && roles.length > 0) {
        writeStorageValue("role", roles[0].role || roles[0].rolecode);
      } else if (resolvedRoleCodes.length > 0) {
        writeStorageValue("role", "Employee");
      } else {
        writeStorageValue("role", null);
      }
    }
    if (menuitems !== undefined) {
      writeStorageValue("menuitems", menuitems == null ? [] : menuitems);
    }
    if (has_admin_access !== undefined) {
      writeStorageValue("has_admin_access", Boolean(has_admin_access));
    }
    if (document_access !== undefined) {
      writeStorageValue("document_access", document_access || null);
    }
    writeStorageValue("is_impersonating", Boolean(is_impersonating));
    if (impersonated_by) writeStorageValue("impersonated_by", impersonated_by);
    else writeStorageValue("impersonated_by", "");
  },

  loadAuthData: () => {
    const userId =
      readStorageValue("userid") || readStorageValue("userId");
    const email = readStorageValue("email");
    const userName =
      readStorageValue("username") || readStorageValue("userName");
    const profilepic = readStorageValue("profilepic");
    const role = readStorageValue("role");
    const tenantId = readStorageValue("tenantid");
    const tenantName = readStorageValue("tenant");
    const subid = readStorageValue("subid");
    const firstname = readStorageValue("firstname");
    const lastname = readStorageValue("lastname");
    const phonenumber = readStorageValue("phonenumber");
    const createdate = readStorageValue("createdate");
    const tenant_created_at = readStorageValue("tenant_created_at");
    const selectedSessionId = readStorageValue("selectedSessionId");
    const menuitems =
      readStorageValue("menuitems") || readStorageValue("menuItems");
    const roles = readStorageValue("roles");
    const role_codes = readStorageValue("role_code");

    return {
      userid: userId ? Number(userId) : null,
      id: userId ? Number(userId) : null,
      email,
      username: userName,
      profilepic,
      role,
      tenantId: tenantId ? Number(tenantId) : null,
      tenant_id: tenantId ? Number(tenantId) : null,
      tenantName,
      tenant_name: tenantName,
      roles,
      role_codes,
      selectedSessionId: selectedSessionId ? Number(selectedSessionId) : null,
      menuitems,
      subid,
      firstname,
      lastname,
      phonenumber,
      createdate,
      tenant_created_at,
    };
  },

  hasAuthData: () => {
    const userId = readStorageValue("userid");
    const email = readStorageValue("email");
    const roleCodes = readStorageValue("role_code");
    return Boolean(userId && email && roleCodes);
  },

  getAuthValue: (key) => readStorageValue(key),

  setAuthValue: (key, value) => {
    writeStorageValue(key, value);
  },
};
